import { Route } from '@angular/router';
import { quotaEntryGuard } from './modules/quotas/quota-entry.guard';
import {
  CanActivateGuard,
  LoginComponent,
  LogoutComponent,
} from '@seech/auth-ng';
import {
  OperationsModulePermissions,
  OperationsRoutes,
} from './constants/operations.constants';
import { LayoutComponent } from './layout/layout.component';
import { operationsPermissionGuard } from './security/guards/operations-permission.guard';

export const appRoutes: Route[] = [
  {
    path: OperationsRoutes.login,
    component: LoginComponent,
  },
  {
    path: OperationsRoutes.logout,
    component: LogoutComponent,
  },
  {
    // Outside the layout, and with no permission guard on it. An account is
    // sent here precisely because it failed that guard, so guarding this route
    // too would bounce it straight back and loop.
    path: OperationsRoutes.noAccess,
    loadComponent: () =>
      import('./modules/no-access/no-access.component').then(
        (m) => m.NoAccessComponent,
      ),
  },
  {
    path: OperationsRoutes.root,
    component: LayoutComponent,
    canActivateChild: [CanActivateGuard.canActivate],
    children: [
      {
        path: OperationsRoutes.dashboard,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.dashboard },
        loadComponent: () =>
          import('./modules/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: OperationsRoutes.integrations,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.integrations },
        loadComponent: () =>
          import('./modules/integrations/integrations.component').then(
            (m) => m.IntegrationsComponent,
          ),
      },
      {
        path: OperationsRoutes.apps,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.apps },
        loadComponent: () =>
          import('./modules/apps/apps.component').then((m) => m.AppsComponent),
      },
      {
        path: OperationsRoutes.feedback,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.feedback },
        loadComponent: () =>
          import('./modules/feedback/feedback.component').then(
            (m) => m.FeedbackComponent,
          ),
      },
      {
        path: OperationsRoutes.legal,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.legal },
        loadComponent: () =>
          import('./modules/legal/legal.component').then(
            (m) => m.LegalComponent,
          ),
      },
      {
        path: OperationsRoutes.featureFlags,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.featureFlags },
        loadComponent: () =>
          import('./modules/feature-flags/feature-flags.component').then(
            (m) => m.FeatureFlagsComponent,
          ),
      },
      {
        path: OperationsRoutes.messageTemplates,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.messageTemplates },
        loadComponent: () =>
          import(
            './modules/message-templates/message-templates.component'
          ).then((m) => m.MessageTemplatesComponent),
      },
      {
        path: OperationsRoutes.observability,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.observability },
        loadComponent: () =>
          import('./modules/observability/observability.component').then(
            (m) => m.ObservabilityComponent,
          ),
      },
      {
        path: OperationsRoutes.alertRecipients,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.alertRecipients },
        loadComponent: () =>
          import('./modules/alert-recipients/alert-recipients.component').then(
            (m) => m.AlertRecipientsComponent,
          ),
      },
      {
        path: OperationsRoutes.quotas,
        canActivate: [quotaEntryGuard],
        loadComponent: () =>
          import('./modules/quotas/quotas.component').then(
            (m) => m.QuotasComponent,
          ),
        loadChildren: () =>
          import('./modules/quotas/quotas.routes').then((m) => m.quotaRoutes),
      },
      {
        path: OperationsRoutes.affiliates,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.affiliates },
        loadComponent: () =>
          import('./modules/affiliates/affiliates.component').then(
            (m) => m.AffiliatesComponent,
          ),
        children: [
          {
            path: OperationsRoutes.affiliateCreators,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.affiliateProgram },
            loadComponent: () =>
              import(
                './modules/affiliates/areas/affiliate-creators.component'
              ).then((m) => m.AffiliateCreatorsComponent),
          },
          {
            path: OperationsRoutes.affiliateDeals,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.affiliateProgram },
            loadComponent: () =>
              import(
                './modules/affiliates/areas/affiliate-deals.component'
              ).then((m) => m.AffiliateDealsComponent),
          },
          {
            path: OperationsRoutes.affiliateLinks,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.affiliateProgram },
            loadComponent: () =>
              import(
                './modules/affiliates/areas/affiliate-links.component'
              ).then((m) => m.AffiliateLinksComponent),
          },
          {
            path: OperationsRoutes.affiliateCommissions,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.affiliateProgram },
            loadComponent: () =>
              import(
                './modules/affiliates/areas/affiliate-commissions.component'
              ).then((m) => m.AffiliateCommissionsComponent),
          },
          {
            path: OperationsRoutes.affiliatePayouts,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.affiliatePayouts },
            loadComponent: () =>
              import(
                './modules/affiliates/areas/affiliate-payouts.component'
              ).then((m) => m.AffiliatePayoutsComponent),
          },
          {
            path: OperationsRoutes.affiliateAudit,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.affiliateProgram },
            loadComponent: () =>
              import(
                './modules/affiliates/areas/affiliate-audit.component'
              ).then((m) => m.AffiliateAuditComponent),
          },
          {
            path: OperationsRoutes.wildcard,
            redirectTo: OperationsRoutes.root,
          },
        ],
      },
      {
        path: OperationsRoutes.merchants,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.merchants },
        loadComponent: () =>
          import('./modules/merchants/merchants.component').then(
            (m) => m.MerchantsComponent,
          ),
      },
      {
        path: OperationsRoutes.usersAccess,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.usersAccess },
        loadComponent: () =>
          import('./modules/users/users.component').then(
            (m) => m.UsersComponent,
          ),
      },
      {
        path: OperationsRoutes.audit,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.audit },
        loadComponent: () =>
          import('./modules/audit/audit.component').then(
            (m) => m.AuditComponent,
          ),
      },
      {
        path: OperationsRoutes.approvals,
        canActivate: [operationsPermissionGuard],
        data: { permissions: OperationsModulePermissions.approvals },
        children: [
          {
            path: OperationsRoutes.root,
            pathMatch: 'full',
            loadComponent: () =>
              import('./modules/approvals/approvals.component').then(
                (m) => m.ApprovalsComponent,
              ),
          },
          {
            path: OperationsRoutes.countryReviews,
            canActivate: [operationsPermissionGuard],
            data: { permissions: OperationsModulePermissions.countryReviews },
            loadComponent: () =>
              import(
                './modules/country-reviews/country-reviews.component'
              ).then((m) => m.CountryReviewsComponent),
          },
          {
            path: OperationsRoutes.wildcard,
            redirectTo: OperationsRoutes.root,
          },
        ],
      },
      {
        // Country reviews used to be a top-level page. Bookmarks and links
        // still arrive here; redirects keep their query string.
        path: OperationsRoutes.countryReviews,
        redirectTo: '/' + OperationsRoutes.approvalCountryReviews,
      },
      {
        path: OperationsRoutes.root,
        redirectTo: OperationsRoutes.dashboard,
        pathMatch: 'full',
      },
      {
        path: OperationsRoutes.wildcard,
        redirectTo: OperationsRoutes.dashboard,
      },
    ],
  },
];
